import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';

const AuthPage = () => {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const navigate = useNavigate();

  const handleTabChange = (tab: 'login' | 'register') => {
    setActiveTab(tab);
    navigate(`/auth/${tab}`);
  };

  return (
    <PageContainer>
      {/* Optimize Edilmiş Arka Plan */}
      <BackgroundLayer>
        <GradientBg />
        <LightPillars>
          {[...Array(5)].map((_, i) => (
            <LightPillar key={i} $index={i} />
          ))}
        </LightPillars>
        <GridOverlay />
      </BackgroundLayer>

      {/* Ana İçerik */}
      <ContentContainer>
        {/* Logo Bölümü */}
        <LogoContainer>
          <MainLogo>
            <LogoGlow />
            <LogoText>
              <GradientText>Indic</GradientText>
              <AccentText>Sigs</AccentText>
            </LogoText>
            <PulseLine />
          </MainLogo>
          <TagLine>
            <TagLineText>AI-Powered Trading Signals</TagLineText>
            <TagLineAccent>Real-Time Analysis</TagLineAccent>
          </TagLine>
        </LogoContainer>

        {/* Form Kartı */}
        <CardContainer>
          <GlassCard>
            {/* Tab Butonları */}
            <TabsContainer>
              <AnimatedTab 
                $isActive={activeTab === 'login'}
                onClick={() => handleTabChange('login')}
              >
                <TabContent>
                  <TabIcon>⚡</TabIcon>
                  <span>Giriş</span>
                </TabContent>
              </AnimatedTab>

              <AnimatedTab 
                $isActive={activeTab === 'register'}
                onClick={() => handleTabChange('register')}
              >
                <TabContent>
                  <TabIcon>✨</TabIcon>
                  <span>Kayıt</span>
                </TabContent>
              </AnimatedTab>
            </TabsContainer>

            {/* Form Alanı */}
            <FormArea>
        <Routes>
          <Route path="login" element={<LoginForm />} />
          <Route path="register" element={<RegisterForm />} />
          <Route path="*" element={<Navigate to="login" replace />} />
        </Routes>
      </FormArea>            
      
      {/* Alt Bilgi */}
            <InfoSection>
              <SecurityBadge>
                <SecurityIcon>🔒</SecurityIcon>
                <SecurityText>End-to-End Encrypted</SecurityText>
              </SecurityBadge>
              <Features>
                <FeatureItem>
                  <FeatureIcon>📊</FeatureIcon>
                  <FeatureText>Real-Time Signals</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>🤖</FeatureIcon>
                  <FeatureText>AI Analysis</FeatureText>
                </FeatureItem>
              </Features>
            </InfoSection>
          </GlassCard>
        </CardContainer>
      </ContentContainer>
    </PageContainer>
  );
};

// Optimize Edilmiş Animasyonlar
const pulseLight = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
`;

const lineMove = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #0A0F1E;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BackgroundLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const GradientBg = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    #0A0F1E 0%,
    #1A1F3E 50%,
    #0A0F1E 100%
  );
`;

const LightPillars = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const LightPillar = styled.div<{ $index: number }>`
  position: absolute;
  width: 2px;
  height: 100%;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(64, 169, 255, 0.2),
    transparent
  );
  left: ${props => (props.$index * 25)}%;
  animation: ${pulseLight} 3s ease-in-out infinite;
  animation-delay: ${props => props.$index * 0.5}s;
`;

const GridOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(64, 169, 255, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(64, 169, 255, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: 0.5;
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  z-index: 1;
`;

const LogoContainer = styled.div`
  text-align: center;
  animation: ${floatAnimation} 6s ease-in-out infinite;
`;

const MainLogo = styled.div`
  position: relative;
  display: inline-block;
  padding: 20px;
`;

const LogoGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150%;
  height: 150%;
  background: radial-gradient(
    circle,
    rgba(64, 169, 255, 0.2) 0%,
    transparent 70%
  );
  filter: blur(20px);
  animation: ${glowPulse} 4s ease-in-out infinite;
`;

const LogoText = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  letter-spacing: 2px;
  position: relative;
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #40A9FF 0%, #9D72FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const AccentText = styled.span`
  color: #40A9FF;
  text-shadow: 0 0 20px rgba(64, 169, 255, 0.5);
`;

const PulseLine = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    #40A9FF,
    transparent
  );
  animation: ${lineMove} 2s linear infinite;
`;

const TagLine = styled.div`
  margin-top: 10px;
`;

const TagLineText = styled.span`
  color: #8B9CC8;
  font-size: 1.2rem;
  letter-spacing: 2px;
  margin-right: 10px;
`;

const TagLineAccent = styled.span`
  color: #40A9FF;
  font-weight: 600;
`;

const CardContainer = styled.div`
  width: 100%;
  max-width: 450px;
`;

const GlassCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(64, 169, 255, 0.2);
  box-shadow: 0 0 30px rgba(64, 169, 255, 0.1);
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
`;

const AnimatedTab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 15px;
  background: ${props => props.$isActive ? 'rgba(64, 169, 255, 0.1)' : 'transparent'};
  border: 1px solid ${props => props.$isActive ? '#40A9FF' : 'transparent'};
  border-radius: 10px;
  color: ${props => props.$isActive ? '#40A9FF' : '#8B9CC8'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(64, 169, 255, 0.1);
    border-color: #40A9FF;
  }
`;

const TabContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const TabIcon = styled.span`
  font-size: 1.2rem;
`;

const FormArea = styled.div`
  margin-bottom: 30px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(64, 169, 255, 0.2);
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #40A9FF;
  font-size: 0.9rem;
`;

const SecurityIcon = styled.span`
  font-size: 1.2rem;
`;

const SecurityText = styled.span`
  color: #8B9CC8;
`;

const Features = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const FeatureIcon = styled.span`
  font-size: 1.2rem;
`;

const FeatureText = styled.span`
  color: #8B9CC8;
  font-size: 0.9rem;
`;

export default AuthPage;